import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import { copy } from '@utils/copy';
import Avatar from '@components/avatar';
import { BASE_IMG_URL } from '@images/imageUrls';
import Toast from '@components/Toast';
import { px2rem } from '@utils/px2rem';
import classNames from 'classnames';
import parse, { domToReact } from 'html-react-parser';
import { formatTime } from '@utils/timeUtils';

interface RouteParams {
    code?: string;
}
interface MessageH5PageProps extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
    userInfo?: any
    unreadNotificationsData?: any
    handleLoadMore: any
    submitNotificationsReadData?: () => Promise<void>
}
interface MessageH5State {
    loading: boolean;
    error: any
    pageLoaded: any
    // navList: any
}
@inject('app', 'site', 'user', 'thread')
@observer
class MessageH5Page extends BasePage<MessageH5PageProps, MessageH5State> {
    private emojiMapCache: Map<string, { url: string; category?: string }> | null = null;
    private emojiListStamp: any = null;
    private isLoadingMore = false;
    state = {
        loading: false,
        error: null,
        pageLoaded: false,

    };
    componentDidMount() {
        const { app, user, thread } = this.props;

        super.componentDidMount();
        window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.onScroll);
    }

    // 滚动触底加载更多私信列表
    onScroll = () => {
        const { user } = this.props;
        const list = user?.listStore?.lists?.['chatList'];
        if (!list || !list.hasMore || list.loading) return;
        if (this.isLoadingMore) return;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        // 距离底部 100px 时触发加载
        if (scrollTop + windowHeight >= documentHeight - 100) {
            this.isLoadingMore = true;
            this.props.handleLoadMore?.();
            // 延迟重置，防止重复触发
            setTimeout(() => {
                this.isLoadingMore = false;
            }, 500);
        }
    }

    // 点击复制
    onCopyClick = (data) => {
        copy(data);
        Toast.success({ content: '已复制社区号' });
    }

    // 打开私信聊天页面
    openDialog = (data, index) => {
        const { user } = this.props;
        // 将该条未读数清零
        if (data.unreadCount > 0 && data.id) {
            user?.listStore?.updateListItem({
                namespace: 'chatList',
                id: data.id,
                updates: { unreadCount: 0 },
            });
        }
        this.props.history.push(`/privateMessage?roomId=${data.roomId}&usercode=${data.receiver?.code}`);
    }

    private escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    private getEmojiMap = () => {
        const emojiList = this.props.site?.emojiList || [];
        if (this.emojiListStamp === emojiList && this.emojiMapCache) return this.emojiMapCache;

        const map = new Map<string, { url: string; category?: string }>();
        emojiList.forEach((cat: any) => {
            (cat?.emoji || []).forEach((emoji: any) => {
                if (emoji?.code && emoji?.url) {
                    map.set(emoji.code, { url: emoji.url, category: cat?.category });
                }
            });
        });
        this.emojiMapCache = map;
        this.emojiListStamp = emojiList;
        return map;
    };

    private replaceEmojiInContent = (content?: string, useSmall: boolean = false) => {
        if (!content) return content || '';
        const map = this.getEmojiMap();
        if (!map || map.size === 0) return content;
        let result = content;
        map.forEach((info, code) => {
            // useSmall=true 时用小表情（评论区），false时根据category判断（正文）
            const cls = useSmall ? styles.emojiInlineSmall : (info.category === '默认' ? styles.emojiInlineSmall : styles.emojiInlineLarge);
            const imgTag = `<img src="${info.url}" alt="${code}" class="${cls}" />`;
            const reg = new RegExp(this.escapeRegex(code), 'g');
            result = result.replace(reg, imgTag);
        });
        return result;
    };

    // 私信列表
    renderChatList = () => {
        const { user, handleLoadMore, unreadNotificationsData } = this.props;
        if (!user || !user.listStore) {
            return null;
        }
        const list = user.listStore.lists['chatList'];
        if (!list) {
            return null;
        }
        const flatList = user.listStore.getList({ namespace: 'chatList' });
        let options = {};
        const parserOptions = {
            replace: (domNode: any) => {
                // 可以在这里自定义节点替换逻辑
            },
            ...options
        };
        return (
            <div style={{ marginBottom: 16 }}>
                <div className={styles.chatListBox}>
                    {flatList.map((item, index) => {
                        return <div key={index} className={styles.chatItem}>
                            <Avatar
                                avatarUrl={item.receiver?.avatar}
                                userCode={item.receiver?.code}
                                size={60}
                                titleMark={item.receiver?.titleMark}
                                level={item.receiver?.level}
                                officialSize={16}
                            ></Avatar>
                            <div className={styles.info} onClick={() => this.openDialog(item, index)}>
                                <div className={styles.infoTop}>
                                    <div className={styles.nickName}>{item.receiver?.nickname}</div>
                                    <div className={styles.time}>{formatTime(item.messageAt, false)}</div>
                                </div>
                                <div className={styles.infoBottom}>
                                    <div className={styles.content}>{parse(this.replaceEmojiInContent(item.lastMessage?.content, true), parserOptions)}</div>
                                    {item.unreadCount > 0 && <div className={styles.num}>{item.unreadCount > 99 ? '99+' : item.unreadCount}</div>}
                                </div>
                            </div>
                        </div>
                    })}
                </div>
            </div>
        )
    }
    // 打开消息列表（评论和@，系统通知，点赞和收藏）
    openNotificationPage = (type) => {
        const { user, match } = this.props;
        // 获取路由参数 code
        const code = match.params.code;
        this.props.history && this.props.history.push(`/notificationsList/${code}?tab=${type}`);
        
    }

    render() {
        let {  } = this.state;
        const { user, userInfo, unreadNotificationsData, match, } = this.props;
        console.log(unreadNotificationsData, '未读消息数')
        let isSelf = match.params?.code == user?.userInfo?.code ? true : false; // 是否是自己
        const isProdEnv = typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
        const relatedAndrepliedUnreadNum = (unreadNotificationsData?.typeUnreadNotifications?.replied + unreadNotificationsData?.typeUnreadNotifications?.related) || 0;  // 评论和@未读消息数
        const systemUnreadNum = (unreadNotificationsData?.typeUnreadNotifications?.system + unreadNotificationsData?.typeUnreadNotifications?.thread + unreadNotificationsData?.typeUnreadNotifications?.post + unreadNotificationsData?.typeUnreadNotifications?.trial_product + unreadNotificationsData?.typeUnreadNotifications?.auction) || 0;  // 系统通知未读消息数
        const likeAndFavoriteUnreadNum = (unreadNotificationsData?.typeUnreadNotifications?.liked + unreadNotificationsData?.typeUnreadNotifications?.favorited) || 0;  // 点赞和收藏未读消息数
        const list = user.listStore.lists['chatList'];
        if (!list) {
            return null;
        }
        const flatList = user.listStore.getList({ namespace: 'chatList' });
        let options = {};
        const parserOptions = {
            replace: (domNode: any) => {
                // 可以在这里自定义节点替换逻辑
            },
            ...options
        };
        return ( <div className={styles.wrapper}>
            <div className={styles.msgTypeList}>
                    <div className={styles.msgItem} onClick={() => this.openNotificationPage(0)}>
                        <div className={styles.iconBox}>
                            {relatedAndrepliedUnreadNum > 0 && <div className={styles.num}>{relatedAndrepliedUnreadNum > 99 ? '99+' : relatedAndrepliedUnreadNum}</div>}
                            <img src={`${BASE_IMG_URL}/message/icon1.png`} alt="评论和@" className={styles.icon}/>
                        </div>
                        <div className={styles.text}>评论和@</div>
                    </div>
                    <div className={styles.msgItem} onClick={() => this.openNotificationPage(1)}>
                        <div className={styles.iconBox}>
                            {systemUnreadNum > 0 && <div className={styles.num}>{systemUnreadNum > 99 ? '99+' : systemUnreadNum}</div>}
                            <img src={`${BASE_IMG_URL}/message/icon2.png`} alt="系统通知" className={styles.icon}/>
                        </div>
                        <div className={styles.text}>系统通知</div>
                    </div>
                    <div className={styles.msgItem} onClick={() => this.openNotificationPage(2)}>
                        <div className={styles.iconBox}>
                            {likeAndFavoriteUnreadNum > 0 && <div className={styles.num}>{likeAndFavoriteUnreadNum > 99 ? '99+' : likeAndFavoriteUnreadNum}</div>}
                            <img src={`${BASE_IMG_URL}/message/icon3.png`} alt="点赞和收藏" className={styles.icon}/>
                        </div>
                        <div className={styles.text}>点赞和收藏</div>
                    </div>
                </div>
                {/* {flatList.length > 0 &&  */}
                <div className={styles.privateTitleBox}>
                    <div className={styles.privateTitle}>私信<span>{unreadNotificationsData.dialogNotifications > 0 && <>（{unreadNotificationsData.dialogNotifications}）</>}</span></div>
                    <img src={`${BASE_IMG_URL}/message/icon4.png`} alt="一键已读" className={styles.readNotifications} onClick={() => this.props.submitNotificationsReadData?.()}/>
                </div>
                {/* } */}
                {flatList.length > 0 && this.renderChatList()}
                {flatList.length == 0 && <div className={styles.nullBox}>
                    <img src={`${BASE_IMG_URL}/common/null.png`} alt="" className={styles.nullIcon} />
                    <div className={styles.nullText}>这里是空的~</div>
                </div>}
        </div>) 
    }
}

export default withRouter(MessageH5Page);
